import { useEffect } from 'react';

/**
 * Scroll-reveal driver. Observes every element carrying
 * [data-reveal] and adds .is-visible the first time it enters
 * the viewport. A MutationObserver picks up elements mounted
 * after the initial render (e.g. content that appears once an
 * API call resolves). CSS owns the transition; the hook only
 * flips the class, so prefers-reduced-motion is honoured in
 * the stylesheet.
 */
const useReveal = (routeKey?: string): void => {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      document
        .querySelectorAll('[data-reveal]')
        .forEach(el => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    const observed = new WeakSet<Element>();
    const observeAll = (): void => {
      document.querySelectorAll('[data-reveal]').forEach(el => {
        if (!observed.has(el)) {
          observed.add(el);
          io.observe(el);
        }
      });
    };

    observeAll();

    const mo = new MutationObserver(observeAll);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [routeKey]);
};

export default useReveal;
